import moment from 'moment';
import { sortColumnsByInt } from '../utils/dualListSelectorUtils';
import { alphaNumeric, decimalNumber, numeric, commaSeperatedNumericValues, validateNumericWithCommas } from 'Utils/validationUtils.js';

function getValidationForDate() {
  return [
    {
      validationRuleKey: 'DATE_RANGE_VALIDATION',
      validationRef: '',
      translatedErrorMessage: 'Start Date must be before End Date. Check the dates and try again.',
      sequence: 0,
    },
  ];
}

export function formatAvailableFilters(filters) {
  let availableFilters = mergeElementIdsForRangeElements(filters);
  return availableFilters.map((filter) => {
    return {
      ...filter,
      updatedId: filter.elementId,
      validations: filter.elementTypeName === 'DATE' ? getValidationForDate() : [],
    };
  });
}

export function calcuateDynamicMultiSelectFiltersCount(filters) {
  let arr = [];
  filters.forEach((data) => {
    if (data.elementTypeName === 'DYNAMIC_MULTITEXT') {
      const index = arr.findIndex((filter) => filter.elementId === data.elementId);
      if (index === -1) {
        arr.push({
          elementId: data.elementId,
          count: filters.filter((filter) => filter.elementId === data.elementId).length,
        });
      }
    }
  });
  return arr;
}

export function addCreateQueueFilterValidations(filters) {
  return filters?.map((filter) => {
    switch (filter?.elementId) {
      case 'cq_name': {
        return {
          ...filter,
          max: 100,
          errorMsg: '',
        };
      }
      case 'cq_acquirer_bin_ica': {
        return {
          ...filter,
          max: 10,
          validate: numeric,
          errorMsg: '',
        };
      }
      case 'cq_associate': {
        return {
          ...filter,
          validate: validateNumericWithCommas,
          errorMsg: '',
        };
      }
      case 'cq_chain': {
        return {
          ...filter,
          validate: validateNumericWithCommas,
          errorMsg: '',
        };
      }
      case 'cq_corp': {
        return {
          ...filter,
          validate: validateNumericWithCommas,
          errorMsg: '',
        };
      }
      case 'cq_region': {
        return {
          ...filter,
          validate: validateNumericWithCommas,
          errorMsg: '',
        };
      }
      case 'cq_mrch_name': {
        return {
          ...filter,
          max: 30,
          errorMsg: '',
        };
      }
      case 'cq_mrch_number': {
        return {
          ...filter,
          validate: commaSeperatedNumericValues,
          errorMsg: '',
        };
      }
      case 'cq_pos_entry_code': {
        return {
          ...filter,
          max: 2,
          validate: alphaNumeric,
          errorMsg: '',
        };
      }
      case 'cq_principal': {
        return {
          ...filter,
          validate: validateNumericWithCommas,
          errorMsg: '',
        };
      }
      case 'mcc_code': {
        return {
          ...filter,
          min: 4,
          max: 4,
          validate: numeric,
          errorMsg: '',
        };
      }
      case 'cq_hierarchy': {
        return {
          ...filter,
          errorMsg: '',
        };
      }
      case 'legal_name': {
        return {
          ...filter,
          max: 50,
          errorMsg: '',
        };
      }
      case 'cq_dispute_amt_frm': {
        return {
          ...filter,
          max: 19,
          validate: (value) => decimalNumber(value, 18),
          errorMsg: '',
        };
      }
      case 'cq_case_age_frm': {
        return {
          ...filter,
          max: 4,
          validate: numeric,
          errorMsg: '',
        };
      }
      case 'cq_days_left_frm': {
        return {
          ...filter,
          max: 2,
          validate: numeric,
          errorMsg: '',
        };
      }
      case 'cq_trnsctn_amt_frm': {
        return {
          ...filter,
          max: 19,
          validate: (value) => decimalNumber(value, 18),
          errorMsg: '',
        };
      }
      case 'cq_day_mrch_sla_frm': {
        return {
          ...filter,
          max: 4,
          validate: numeric,
          errorMsg: '',
        };
      }
      case 'cq_prd_sla_cntr_frm': {
        return {
          ...filter,
          max: 4,
          validate: numeric,
          errorMsg: '',
        };
      }
      case 'cq_tax_id': {
        return {
          ...filter,
          errorMsg: '',
        };
      }
      case 'cq_group': {
        return {
          ...filter,
          validate: validateNumericWithCommas,
          errorMsg: '',
        };
      }
      default:
        return filter;
    }
  });
}

export function formatActiveFilters(filters) {
  const activeFilters = mergeElementIdsForRangeElements(filters);

  //calculating multi select filters with count
  let multiSelectFiltersWithCount = calcuateDynamicMultiSelectFiltersCount(activeFilters);
  let multiSelectFilters = [];

  //assigning IDs to multi select active filters
  multiSelectFiltersWithCount.forEach((data) => {
    let filteredArray = activeFilters
      .filter((activeFilter) => activeFilter.elementId === data.elementId)
      .map((activeFilter, index) => {
        return {
          ...activeFilter,
          updatedId: `${activeFilter.elementId}@${index + 1}`,
          validations: [],
        };
      });
    multiSelectFilters = multiSelectFilters.concat(filteredArray);
  });

  //assigning IDs to active other filters
  let otherFilters = activeFilters
    .filter((activeFilter) => activeFilter.elementTypeName !== 'DYNAMIC_MULTITEXT')
    .map((otherFilter) => {
      return {
        ...otherFilter,
        updatedId: otherFilter.elementId,
        validations: otherFilter.elementTypeName === 'DATE' ? getValidationForDate() : [],
      };
    });

  return otherFilters.concat(multiSelectFilters);
}

export function mergeElementIdsForRangeElements(filters) {
  let targetObjects = [];
  filters.forEach((filterElement1) => {
    let toElementClone = {};

    if (filterElement1.elementCategoryName === 'FROM') {
      const toElement = filters.find((filterElement2) => {
        return (
          filterElement2.elementSequenceNumber === filterElement1.elementSequenceNumber &&
          filterElement2.elementCategoryName === 'TO'
        );
      });

      toElementClone = { ...toElement };
      toElementClone.elementId = `${filterElement1.elementId}-${toElement.elementId}`;
      toElementClone.elementCategoryName = `${filterElement1.elementCategoryName}-${toElement.elementCategoryName}`;
      if (filterElement1.elementValue !== null && filterElement1.elementValue !== undefined) {
        toElementClone.elementValue = filterElement1.elementValue;
        toElementClone['elementValue1'] = toElement.elementValue;
      }
      if (filterElement1?.max) {
        toElementClone.max = filterElement1?.max;
      }
      if (filterElement1?.validate) {
        toElementClone.validate = filterElement1.validate;
        toElementClone.errorMsg = filterElement1.errorMsg;
      }
      targetObjects = targetObjects.concat(toElementClone);
    } else if (filterElement1.elementCategoryName !== 'TO') {
      targetObjects = targetObjects.concat(filterElement1);
    }
  });
  return targetObjects;
}

export function splitMergedElementIdsFilters(filters) {
  let activeFilters = [];
  filters.forEach((filter) => {
    if (filter.elementCategoryName === 'FROM-TO') {
      const idArray = filter.elementId.split('-');
      const categoryArray = filter.elementCategoryName.split('-');
      const updatedIdArray = filter.updatedId.split('-');

      idArray.forEach((id, index) => {
        activeFilters = activeFilters.concat({
          ...filter,
          elementCategoryName: categoryArray[index],
          elementId: idArray[index],
          updatedId: updatedIdArray[index],
        });
      });
    } else {
      delete filter.filterValues;
      activeFilters = activeFilters.concat(filter);
    }
  });
  return activeFilters;
}

// maintaining structure for current values
export function mapActiveFilters(filters = [], data = []) {
  const results = [];
  filters
    .filter((filtr) => filtr !== undefined)
    .forEach((filter) => {
      const matchingFilter = data.find((column) => column.updatedId === filter.id);
      if (matchingFilter && (matchingFilter.elementLabel || matchingFilter.name)) {
        results.push(
          Object.assign({}, filter, {
            elementId: matchingFilter.elementId,
            elementMandatory: matchingFilter.elementMandatory,
            updatedId: matchingFilter.updatedId,
            elementSection: matchingFilter.elementSection,
            actualNameId: matchingFilter.actualNameId,
            elementSequenceNumber: matchingFilter.elementSequenceNumber,
            name: matchingFilter.elementLabel ? matchingFilter.elementLabel : matchingFilter.name,
            value: !filter.v2 ? filter.v1 : null,
            values: filter.v2 ? { v1: filter.v1, v2: filter.v2 } : null,
            v1: filter.v1,
            v2: filter.v2,
            category: matchingFilter.elementCategoryName ? matchingFilter.elementCategoryName : matchingFilter.category,
            elementType: matchingFilter.elementTypeName ? matchingFilter.elementTypeName : matchingFilter.elementType,
          }),
        );
      }
    });
  return results;
}

export function convertJsonObjectToArray(newValues, filterList = []) {
  let newFilterValues = [];
  newFilterValues = Object.keys(newValues).map((key) => {
    const metaData = filterList.find((filter) => {
      return filter.updatedId === key;
    });
    if (metaData) {
      return Object.assign({}, newValues[key], {
        id: key,
        elementCategoryName: metaData.elementCategoryName ? metaData.elementCategoryName : metaData.category,
        elementTypeName: metaData.elementTypeName ? metaData.elementTypeName : metaData.elementType,
      });
    }
  });
  return newFilterValues;
}

export function splitMergedElementIdsFilterValues(filters, activeFilters = [], corp, region, filterTypeId) {
  let filterValues = prepareSaveFitlerValueResponse(filters, corp, region, filterTypeId);
  let nonFilterValues = [];

  let arr = activeFilters.filter((activeFilter) => {
    // eslint-disable-next-line no-unused-expressions
    !filters.find((data) => data.id === activeFilter.updatedId);
  });

  if (arr.length > 0) {
    nonFilterValues = prepareSaveFitlerValueResponse(arr, corp, region);
  }
  return filterValues.concat(nonFilterValues);
}

function prepareSaveFitlerValueResponse(filters, corp, region, filterTypeId) {
  let filterValues = [];
  let corpList = [];
  let regionList = [];
  let elementSequenceNumber = [5,9,14];
  filters.forEach((filter) => {
    if (
      (filter.elementId === 'cq_reason_code' || (filter.elementId === 'reason_code' && elementSequenceNumber.includes(filter.elementSequenceNumber))) &&
      filter.v1.length > 0 && filter.elementSection !== "create_queue"
    ) {
      filter.v1 = getCardBrandWithReasonCodes(filter.v1);
    }

    if (
      (filter.elementId === 'cq_reason_code' || filter.elementId === 'reason_code') &&
      filter.v1.length > 0 &&
      typeof filter.v1[0] === 'string' &&
      filter.v1[0].includes('-')
    ) {
      filter.v1 = filter.v1.map((reasonCode) =>
        typeof reasonCode === 'string' && reasonCode.includes('-') ? reasonCode.split('-')[0] : reasonCode,
      );
    }

    if (filter.elementCategoryName === 'FROM-TO') {
      const idArray = filter.id ? filter.id.split('-') : filter.updatedId.split('-');
      idArray.forEach((id, index) => {
        const filterValue = filter[`v${index + 1}`];
        filterValues = filterValues.concat({
          elementId: idArray[index],
          elementValue:
            filter.elementTypeName === 'DATE'
              ? changeDateFormat(filterValue, 'YYYY-MM-DD')
              : filterValue
              ? filterValue
              : '',
          actualNameId: filter.actualNameId,
        });
      });
    } else if (filter.elementTypeName === 'DYNAMIC_MULTITEXT' && filter.v1 !== '') {
      //defect id 44  clearing drop down value of multiselect and prevents to submit empty array
      filterValues = filterValues.concat({
        elementId: filter.id ? filter.id.split('@')[0] : filter.updatedId.split('@')[0],
        elementValue: filter.v1 ? filter.v1 : '',
        actualNameId: filter.actualNameId,
      });
    } else if (filter.id === 'corpregion') {
      if (corp !== undefined && corp.length !== 0) {
        filterValues = filterValues.concat({
          elementId: 'corp',
          elementValue: corp,
          actualNameId: filter.actualNameId,
        });
      } else if (filter.v1 !== undefined && filter.v1.length !== 0) {
        filter.v1.map((filterCorp) => {
          filterCorp.map((corpObj) => {
            corpList.push(JSON.parse(JSON.stringify(corpObj))['corp']);
          });
        });
        filterValues = filterValues.concat({
          elementId: 'corp',
          elementValue: corpList,
          actualNameId: filter.actualNameId,
        });
      }
      if (region !== undefined && region.length !== 0) {
        filterValues = filterValues.concat({
          elementId: 'region',
          elementValue: region,
          actualNameId: filter.actualNameId,
        });
      } else if (filter.v1 !== undefined && filter.v1.length !== 0) {
        filter.v1.map((filterRegion) => {
          filterRegion.map((regionObj) => {
            regionList.push(JSON.parse(JSON.stringify(regionObj))['regionCode']);
          });
        });
        filterValues = filterValues.concat({
          elementId: 'region',
          elementValue: regionList,
          actualNameId: filter.actualNameId,
        });
      }
    } else if (filter.elementTypeName === 'TWO_MULTISELECT') {
      filterValues = filterValues.concat({
        elementId: filter.id ? filter.id : filter.updatedId,
        elementValue: filter.v1 ? filter.v1 : null,
        elementValueSecondField: filter.v2 ? filter.v2 : null,
      });
    } else if (filter.v1 !== undefined && filter.v1.length !== 0) {
      //defect id 44  clearing drop down value of multiselect and prevents to submit empty array

      filterValues = filterValues.concat({
        elementId: filter.id ? filter.id : filter.updatedId,
        elementValue: filter.v1 ? filter.v1 : '',
        actualNameId: filter.actualNameId,
      });
    }
  });
  return filterValues;
}

function getCardBrandWithReasonCodes(reasonCodes) {
let arrayOfReasonCodes = [];
let cardBrandWithReasonCodes= [];

if(typeof reasonCodes[0] === 'object') {
  if ('cardBrandCode' in reasonCodes[0]) {
    return reasonCodes;
  }
  cardBrandWithReasonCodes = reasonCodes.reduce((reasonCodeMap, reasonCodeFilter) => {
    const groupId = reasonCodeFilter.groupId;
    if (!reasonCodeMap[groupId]) {
      reasonCodeMap[groupId] = [];
    }
    reasonCodeMap[groupId].push(reasonCodeFilter.id.includes('-') ? reasonCodeFilter.id.split('-')[0] : reasonCodeFilter.id);
    return reasonCodeMap;
  }, {});  
}
else {
  cardBrandWithReasonCodes= reasonCodes.reduce((codes, item) => {
    const [reasonCode, cardbrand] = item.split('-');
    if (codes[cardbrand]) {
      codes[cardbrand].push(reasonCode);
    } else {
      codes[cardbrand] = [reasonCode];  
    }
    return codes;
  }, {});
}
Object.keys(cardBrandWithReasonCodes).forEach((k) => {
  arrayOfReasonCodes.push({ cardBrandCode: k, reasonCodes: cardBrandWithReasonCodes[k] });
});
return arrayOfReasonCodes;
}

export function mergeSavedElementIdsForRangeElements(savedFilters) {
  let finalSavedFilters = [];
  savedFilters.forEach((savedFilter) => {
    let filterParams = formatActiveFilters(savedFilter.filterParams);
    let targetObjects = [];
    filterParams.forEach((filterElement1) => {
      const toElementClone = {};

      if (filterElement1.elementTypeName === 'TWO_MULTISELECT') {
        toElementClone.id = `${filterElement1.updatedId}`;
        toElementClone.v1 = filterElement1.elementValue?.v1;
        toElementClone.v2 = filterElement1.elementValue?.v2;
        targetObjects = targetObjects.concat(toElementClone);
      } else if (filterElement1.elementCategoryName === 'FROM-TO') {
        if (filterElement1.elementTypeName === 'DATE' && filterElement1.elementValue !== '') {
          const dateFormat = 'MM/DD/YYYY';
          toElementClone.id = `${filterElement1.updatedId}`;
          toElementClone.v1 = filterElement1.elementValue
            ? changeDateFormat(filterElement1.elementValue, dateFormat)
            : '';
          toElementClone.v2 = filterElement1.elementValue1
            ? changeDateFormat(filterElement1.elementValue1, dateFormat)
            : '';
          targetObjects = targetObjects.concat(toElementClone);
        } else if (
          (filterElement1.elementTypeName === 'CURRENCYRANGE' && filterElement1.elementValue !== '') ||
          filterElement1.elementValue1 !== ''
        ) {
          toElementClone.id = `${filterElement1.updatedId}`;
          toElementClone.v1 = filterElement1.elementValue ? `${filterElement1.elementValue}` : '';
          toElementClone.v2 = filterElement1.elementValue1 ? `${filterElement1.elementValue1}` : '';
          targetObjects = targetObjects.concat(toElementClone);
        }
      } else if (
        filterElement1.elementCategoryName !== 'FROM-TO' &&
        filterElement1.elementValue &&
        filterElement1.elementValue !== ''
      ) {
        if (`${filterElement1.updatedId}` === 'corpregion') {
          toElementClone.v1 = filterElement1.elementValue;
        } else {
          toElementClone.v1 = `${filterElement1.elementValue}`;
        }
        toElementClone.id = `${filterElement1.updatedId}`;
        targetObjects = targetObjects.concat(toElementClone);
      }
    });
    finalSavedFilters = finalSavedFilters.concat({
      ...savedFilter,
      filterParams: targetObjects,
      activeFilters: savedFilter.filterParams,
    });
  });
  return finalSavedFilters;
}

export function mapSavedFilters(filters, filtersToApply = []) {
  return filters.map((filter) => {
    const filterJson = filter.filterParams; // JSON.parse(filter.filterJson);
    const match = filtersToApply.filter((appliedFilter) => {
      return filterJson.some((f) => {
        if (f.id === appliedFilter.id) {
          // both of these will clear out an already applied filter if a new filter is
          // applied with different values.

          // if there is a singular value, it is equal, with no secondary value.
          if (f.v1 && f.v1 === appliedFilter.v1 && !f.v2) {
            return true;
          }

          // if there is a range of values, it is equal on both values.
          if (f.v1 && f.v2 && f.v1 === appliedFilter.v1 && f.v2 === appliedFilter.v2) {
            return true;
          }
        }

        return false;
      });
    });

    return Object.assign({}, filter, {
      isActive: match.length > 0 && match.length === filterJson.length ? match : false,
    });
  });
}

export function changeDateFormat(filterValue, dateFormat) {
  return filterValue
    ? filterValue.split('/').length > 1 || filterValue.split('-').length > 1
      ? moment(filterValue).format(dateFormat)
      : filterValue
    : '';
}

export function removeFilterActionSavedFilter(availableFilters, activeFilters, filtersValuesToUse) {
  let newAvailableColumns = [];

  // 1. Remove all savedFilter from availableFilter list
  // eslint-disable-next-line no-unused-vars
  let keysInActiveColumns = activeFilters.length > 0 && activeFilters.map((eachColumn) => eachColumn.elementId);
  availableFilters = availableFilters.filter((availFilter) => {
    let duplicateFlag = true;
    for (let saveIndex = 0; saveIndex < filtersValuesToUse.length; saveIndex++) {
      if (filtersValuesToUse[saveIndex].id === availFilter.elementId) {
        duplicateFlag = false;
        break;
      }
    }
    return duplicateFlag;
  });

  // 2. Compare active filter and save filter and those in newAvailableColumn list which are not present
  newAvailableColumns = activeFilters.filter((activeFilter) => {
    let duplicateFlag = true;
    for (let saveIndex = 0; saveIndex < filtersValuesToUse.length; saveIndex++) {
      if (filtersValuesToUse[saveIndex].id === activeFilter.elementId) {
        duplicateFlag = false;
        break;
      }
    }
    return duplicateFlag;
  });

  // 3. Compare if newAvailable columns are present or not in available filter
  newAvailableColumns = newAvailableColumns.filter((newFilter) => {
    let duplicateFlag = true;
    for (let saveIndex = 0; saveIndex < availableFilters.length; saveIndex++) {
      if (availableFilters[saveIndex].elementId === newFilter.elementId) {
        duplicateFlag = false;
        break;
      }
    }
    return duplicateFlag;
  });

  // 4. active filter not present in save filter add that in available filter
  newAvailableColumns = sortColumnsByInt([...availableFilters, ...newAvailableColumns], 'elementSequenceNumber');
  // let displayAvailableColumns = newAvailableColumns.filter(eachItem=>!eachItem.elementId.includes(keysInActiveColumns))
  return newAvailableColumns;
}

export function splitMergedElementIdsFilterValuesSaveFilter(filters, activeFilters = [], corpRegion) {
  let filterValues = prepareSaveFitlerValueResponseCorpRegion(filters, corpRegion);
  let nonFilterValues = [];

  let arr = activeFilters.filter((activeFilter) => {
    // eslint-disable-next-line no-unused-expressions
    !filters.find((data) => data.id === activeFilter.updatedId);
  });

  if (arr.length > 0) {
    nonFilterValues = prepareSaveFitlerValueResponseCorpRegion(arr, corpRegion);
  }

  return filterValues.concat(nonFilterValues);
}

function prepareSaveFitlerValueResponseCorpRegion(filters, corpRegion) {
  let filterValues = [];
  filters.forEach((filter) => {
    if (filter.elementCategoryName === 'FROM-TO') {
      const idArray = filter.id ? filter.id.split('-') : filter.updatedId.split('-');
      idArray.forEach((id, index) => {
        const filterValue = filter[`v${index + 1}`];
        filterValues = filterValues.concat({
          elementId: idArray[index],
          elementValue:
            filter.elementTypeName === 'DATE'
              ? changeDateFormat(filterValue, 'YYYY-MM-DD')
              : filterValue
              ? filterValue
              : '',
        });
      });
    } else if (filter.elementTypeName === 'DYNAMIC_MULTITEXT' && filter.v1 !== '') {
      //defect id 44  clearing drop down value of multiselect and prevents to submit empty array
      filterValues = filterValues.concat({
        elementId: filter.id ? filter.id.split('@')[0] : filter.updatedId.split('@')[0],
        elementValue: filter.v1 ? filter.v1 : '',
      });
    } else if (filter.id === 'corpregion') {
      if (corpRegion !== undefined && corpRegion.length !== 0) {
        filterValues = filterValues.concat({
          elementId: 'corpregion',
          elementValue: corpRegion,
        });
      }
    } else if (filter.elementTypeName === 'TWO_MULTISELECT') {
      filterValues = filterValues.concat({
        elementId: filter.id ? filter.id : filter.updatedId,
        elementValue: filter.v1 ? filter.v1 : null,
        elementValueSecondField: filter.v2 ? filter.v2 : null,
      });
    }
     else if (filter.id === 'cq_reason_code' || (filter.id === 'reason_code' && filter.elementSequenceNumber == 5)) {
      filterValues = filterValues.concat({
        elementId: filter.id ? filter.id : filter.updatedId,
        elementValue: filter.v1 ? filter.v1.map(e =>{ return e.value ? !e.value.includes('-') ? e.value + "-" + e.groupId : e.value : e}) : '',
      });
    } 
    else if (filter.v1 !== undefined && filter.v1.length !== 0) {
      //defect id 44  clearing drop down value of multiselect and prevents to submit empty array
      filterValues = filterValues.concat({
        elementId: filter.id ? filter.id : filter.updatedId,
        elementValue: filter.v1 ? filter.v1 : '',
      });
    }
  });
  return filterValues;
}
